import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { useNavigate } from "react-router-dom";
import { tokens } from "../../../theme";

/**
 * Execute page — Federated Data Review card.
 *
 * React-native reproduction of the backend mock artifact
 * `Backend_To_Frontend/data_review_popup.html`: a per-site schema/data review
 * with PASS / WARN / FAIL severity per finding. Renders for any non-pass
 * result (warn = advisory, fail = blocking). For a pass result it returns null
 * — the Pre-run Checks row already conveys the green state.
 *
 * Prototype-only. The numbers come from the mock validator
 * (mockSchemaValidation.js); no real backend / artifact endpoint is queried.
 */

// Token-based semantic tints so the validation rows read correctly in light
// AND dark (1.1/1.4). Alpha fills sit over whatever surface they land on.
const SEVERITY_STYLE = {
  pass: { bg: "var(--fx-success-soft)", border: "var(--fx-success-border)", fg: "var(--fx-success-text)", chipBg: "var(--fx-success-soft)" },
  warn: { bg: "var(--fx-warning-soft)", border: "var(--fx-warning-border)", fg: "var(--fx-warning-text)", chipBg: "var(--fx-warning-soft)" },
  fail: { bg: "var(--fx-danger-soft)",  border: "var(--fx-danger-border)",  fg: "var(--fx-danger-text)",  chipBg: "var(--fx-danger-soft)" },
};

// Known finding `code` values emitted by the real AKS DEV backend across
// dev-0.7.0 → dev-0.7.7 (verified empirically by Phase 2 / 2.2 / Phase 1
// dev-0.7.3 / Phase 1.1 dev-0.7.4 / BRCA dev-0.7.7 smoke runs). Mock
// validator does not emit `code`. Unknown codes still render with a
// generic "Finding code: <code>" tooltip via the badge fallback.
//
// Blocking vs non-blocking is driven by the response's `blocked` flag
// (see execute/index.jsx training-start gate). Tooltips below explicitly
// tag blocking codes (l2_data for tabular data fails, l2_contract for
// imaging schema-contract fails).
const CODE_TOOLTIPS = {
  // Stable across all dev-0.7.x baselines
  SCHEMA_FILE_IS_MOCK:
    "DEV schema is a mock, expected during the current Schema v1 contract phase.",
  READINESS_WARMING_UP:
    "Site capability is currently warming up. Validation may still proceed.",

  // dev-0.7.2 (tabular runtime gap)
  DATA_FILE_NOT_READABLE:
    "Backend reached the site-local data inspection step but cannot read the data file in this DEV runtime. Non-blocking, detailed feature, label, and sample-level findings will surface once site-local data access lands.",
  IMAGING_FILE_VALIDATION_DEFERRED:
    "Imaging file-level validation is deferred, image / mask / PVC inspection not yet enforced in this DEV runtime.",
  IMAGING_VALIDATION_DEFERRED:
    "Imaging validation is deferred, full image / mask / PVC validation not yet enforced. (Older code name; canonical dev-0.7.2+ code is IMAGING_FILE_VALIDATION_DEFERRED.)",
  CHANNELS_REGISTRY_RUNTIME_DRIFT:
    "Channel metadata in the registry differs from runtime observation. Non-blocking in this DEV phase.",

  // dev-0.7.3 (first controlled tabular data-vs-schema demo)
  EXTRA_COLUMNS_IGNORED:
    "Site-local data contains columns not in the schema; they are ignored during training. Non-blocking, review the message for the column names if listed.",
  MISSING_LABEL_CLASS:
    "Site-local data has fewer label classes than the schema declares. Non-blocking in DEV, training can still proceed; aggregation will see no samples for the missing class at this site.",
  READINESS_NEEDS_ATTENTION:
    "Site capability is flagged for review. Non-blocking by itself, pair with any other fail-severity finding in the same row to understand the full picture.",
  MISSING_REQUIRED_FEATURE:
    "Site-local data is missing a required feature column declared by the schema. Blocks training (blocked=true, abortStage=l2_data), the message names the specific missing column.",
  LABEL_OUT_OF_RANGE:
    "Site-local data contains label values outside the declared label_space. Blocks training (blocked=true, abortStage=l2_data), fix the data labels or the schema's label_space before re-running.",

  // dev-0.7.4 (first controlled imaging schema-contract demo)
  MASK_ENCODING_INVALID:
    "The imaging schema's mask_encoding declaration is not one of the allowed values. Blocks training (blocked=true, abortStage=l2_contract), the message names the bad value and the allowed list.",
  MASK_SHAPE_INVALID:
    "The imaging schema's mask_shape is inconsistent with its mask_encoding. Blocks training (blocked=true, abortStage=l2_contract), the message names the specific dimension mismatch.",
};

// Optional UI-side reframe for codes whose backend `title` / `message` are
// technically accurate but easy to misread. Each entry replaces what the
// finding row shows; the backend's original string is dropped from view
// (still on the wire / in DevTools). Use sparingly — only for known
// DEV-phase signals where the backend wording risks suggesting that the
// entire validation result is unreliable.
const CODE_FRIENDLY_OVERRIDES = {
  // dev-0.7.3+: backend labels every DEV schema as a mock. With dev-0.7.3
  // the data-vs-schema layer is real (real n_samples, real label_hist,
  // real missing-feature detection). The user should not read "DEV/mock"
  // as "the whole result is fake".
  SCHEMA_FILE_IS_MOCK: {
    title: "DEV schema contract",
    message:
      "This check used a development Schema v1 contract. The data-level validation result still applies, but the schema contract is not yet marked as authoritative.",
  },
};

const StatusChip = ({ status }) => {
  const s = SEVERITY_STYLE[status] || SEVERITY_STYLE.pass;
  const label = status === "pass" ? "PASS" : status === "warn" ? "WARN" : "FAIL";
  const Icon =
    status === "pass" ? CheckCircleIcon : status === "warn" ? WarningAmberIcon : ErrorOutlineIcon;
  return (
    <Chip
      size="small"
      icon={<Icon sx={{ fontSize: "13px !important", color: `${s.fg} !important` }} />}
      label={label}
      sx={{
        bgcolor: s.chipBg,
        color: s.fg,
        fontWeight: 800,
        fontSize: "0.6rem",
        letterSpacing: 0.5,
        height: 20,
      }}
    />
  );
};

const Kpi = ({ label, value, color, colors }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1,
      borderRadius: 1.25,
      border: `1px solid ${colors.grey?.[800] || "var(--fx-border)"}`,
      bgcolor: "var(--fx-paper)",
      textAlign: "center",
      minWidth: 70,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: colors.grey?.[500] || "var(--fx-text-muted)",
        fontWeight: 700,
        fontSize: "0.58rem",
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontWeight: 800, fontSize: "1.15rem", color: color || "var(--fx-text-strong)", lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Box>
);

const SchemaValidationDetailsCard = ({ result, onReRun, retrying }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  // dev-0.7.11 — render the card for pass / warn / fail. Previously we
  // returned null on pass; user feedback (2026-06-04) wanted the same
  // detailed per-site breakdown visible on /execute even when both sites
  // are clean PASS, so the user has consistent affordance across pass /
  // warn / fail. `not_ready` (e.g. validation service transport error)
  // still returns null — there's no useful content to render in that case.
  if (!result || result.overall === "not_ready") return null;

  const isFail = result.overall === "fail";
  const isPass = result.overall === "pass";
  const headStyle = isFail
    ? SEVERITY_STYLE.fail
    : isPass
    ? SEVERITY_STYLE.pass
    : SEVERITY_STYLE.warn;
  const counts = result.counts || { pass: 0, warn: 0, fail: 0 };
  const header = result.schemaHeader;

  // Two label_hist shapes observed in DEV:
  //   (a) numeric-string keys mapped through label_space  — the client mock
  //       validator emits this: { "0": 20, "1": 20, ... }
  //   (b) label-name keys directly                         — the AKS DEV
  //       backend emits this for dev-0.7.3+: { "Breast": 20, "Alive": 420 }
  // Detect by parsing the key as a number; only resolve through label_space
  // when the key is a clean integer index. Otherwise treat the key as the
  // already-human-readable label name. Fixes the prior "NaN: 420" bug where
  // string keys were coerced to NaN and used to index into label_space.
  const fmtHist = (hist, labelSpace) => {
    if (!hist || typeof hist !== "object") return null;
    return Object.entries(hist)
      .map(([k, v]) => {
        const idx = Number(k);
        const isIntegerIndex =
          Number.isInteger(idx) && String(idx) === String(k);
        const name =
          isIntegerIndex && labelSpace && labelSpace[idx] != null
            ? labelSpace[idx]
            : k;
        return `${name}: ${v}`;
      })
      .join("  ·  ");
  };

  return (
    <Card sx={{ mb: 2, border: `1px solid ${headStyle.border}`, borderRadius: 2, boxShadow: 'var(--fx-shadow-sm)', overflow: "hidden" }}>
      {/* Header band — gradient color tracks the overall severity:
            fail → deep red, warn → amber, pass → forest green (dev-0.7.11). */}
      <Box
        sx={{
          px: 3,
          py: 2,
          background: isFail
            ? "linear-gradient(90deg, #7f1d1d, #b91c1c)"
            : isPass
            ? "linear-gradient(90deg, #14532d, #15803d)"
            : "linear-gradient(90deg, #78350f, #b45309)",
          color: "#ffffff",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <FactCheckIcon />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2, color: "#ffffff" }}>
                Federated Data Review
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {isFail
                  ? "Schema validation failed, training is blocked"
                  : isPass
                  ? "Schema validation passed, all selected sites are training-ready"
                  : "Schema validation raised warnings, training may proceed"}
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={isFail ? "FAIL" : isPass ? "PASS" : "WARN"}
            sx={{
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#ffffff",
              fontWeight: 800,
              letterSpacing: 0.6,
            }}
          />
        </Stack>
      </Box>

      <CardContent sx={{ p: 3 }}>
        {/* KPI row — counts from backend are finding-severity counts (not
            site verdicts), so for a clean PASS run they read 0/0/0 which
            visually looks wrong. Recompute per-site rollups from
            siteResults[].status when overall is pass, so the user sees
            "Pass: N" matching what their eye expects. Warn/fail paths
            keep using the backend's finding-severity counts because there
            the row count and finding count tend to align. */}
        <Stack direction="row" spacing={1.25} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Kpi label="Clients" value={result.siteResults.length} colors={colors} />
          <Kpi
            label="Pass"
            value={
              isPass
                ? result.siteResults.filter((r) => r.status === "pass").length
                : counts.pass
            }
            color="var(--fx-success-text)"
            colors={colors}
          />
          <Kpi label="Warn" value={counts.warn} color="var(--fx-warning-text)" colors={colors} />
          <Kpi label="Fail" value={counts.fail} color="var(--fx-danger-text)" colors={colors} />
        </Stack>

        {/* Abort bar (fail only). dev-0.7.3+ exposes a top-level
            `abortStage` field on blocked responses; dev-0.7.4 added a
            second value for imaging contract failures:
              "l2_data"     → tabular data-vs-schema (missing column,
                              label out of range)
              "l2_contract" → imaging schema-contract (mask_encoding /
                              mask_shape declared invalid in the schema)
            Older backends do not emit `abortStage`; we fall back to
            "prelaunch" to preserve the prior label. */}
        {isFail && result.abortReason && (
          <Box
            sx={{
              px: 1.5,
              py: 1,
              mb: 2,
              borderRadius: 1,
              bgcolor: "var(--fx-danger-soft)",
              border: "1px solid var(--fx-danger-border)",
            }}
          >
            <Typography variant="caption" sx={{ color: "var(--fx-danger-text)", fontWeight: 700, display: "block" }}>
              {result.abortStage === "l2_data"
                ? "Abort stage: data validation (l2_data)"
                : result.abortStage === "l2_contract"
                ? "Abort stage: schema contract validation (l2_contract)"
                : result.abortStage
                ? `Abort stage: ${result.abortStage}`
                : "Abort stage: prelaunch"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "var(--fx-danger-text)", fontFamily: "ui-monospace, monospace", fontSize: "0.72rem" }}
            >
              Abort reason: {result.abortReason}
            </Typography>
          </Box>
        )}

        {/* Schema fingerprint */}
        {header && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, auto)" },
              gap: 2,
              mb: 2.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "var(--fx-subtle)",
              border: `1px solid ${colors.grey?.[800] || "var(--fx-border)"}`,
            }}
          >
            {[
              ["schema_id", header.schema_id],
              ["feature_dim", header.feature_dim ?? "Not set"],
              ["num_classes", header.num_classes],
              ["modality", header.modality],
            ].map(([k, v]) => (
              <Box key={k}>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.grey?.[500] || "var(--fx-text-muted)",
                    fontWeight: 700,
                    fontSize: "0.58rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    display: "block",
                  }}
                >
                  {k}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    color: "var(--fx-text-strong)",
                    wordBreak: "break-all",
                  }}
                >
                  {String(v)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            color: colors.grey?.[500] || "var(--fx-text-muted)",
            fontWeight: 700,
            fontSize: "0.6rem",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            display: "block",
            mb: 1,
          }}
        >
          Per-site review
        </Typography>

        {/* Per-site rows */}
        <Stack spacing={1.25}>
          {result.siteResults.map((r) => {
            const s = SEVERITY_STYLE[r.status] || SEVERITY_STYLE.pass;
            const histStr = fmtHist(r.label_hist, header?.label_space);
            return (
              <Box
                key={`${r.site_id}::${r.capability_id}`}
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.25,
                  bgcolor: s.bg,
                  border: `1px solid ${s.border}`,
                }}
              >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <StatusChip status={r.status} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "var(--fx-text-strong)" }} noWrap>
                        {r.site_display_name}
                      </Typography>
                    </Stack>
                    {r.organization_display_name && (
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey?.[500] || "var(--fx-text-muted)", display: "block", mt: 0.25 }}
                      >
                        {r.organization_display_name}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title="capability_id">
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: "monospace",
                        color: colors.grey?.[500] || "var(--fx-text-muted)",
                        fontSize: "0.65rem",
                        flexShrink: 0,
                      }}
                    >
                      {r.capability_id}
                    </Typography>
                  </Tooltip>
                </Stack>

                {/* sample / histogram line */}
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.75,
                    color: colors.grey?.[600] || "var(--fx-text-secondary)",
                    fontSize: "0.72rem",
                  }}
                >
                  {r.n_samples != null && (
                    <>
                      n_samples: <strong>{r.n_samples.toLocaleString()}</strong>
                    </>
                  )}
                  {histStr && (
                    <>
                      {r.n_samples != null ? "  ·  " : ""}label_hist: {histStr}
                    </>
                  )}
                  {r.n_samples == null && !histStr && "No sample-distribution data reported."}
                </Typography>

                {/* findings — sorted fail-first so the blocking root cause
                    is at the top of the row, with warn (incl. informational
                    DEV codes) below it. Stable sort within severity. */}
                {r.findings?.length > 0 && (
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {[...r.findings]
                      .map((f, i) => ({ f, i }))
                      .sort((a, b) => {
                        const rank = { fail: 0, warn: 1, pass: 2 };
                        const sa = rank[a.f.severity] ?? 9;
                        const sb = rank[b.f.severity] ?? 9;
                        return sa === sb ? a.i - b.i : sa - sb;
                      })
                      .map(({ f }, i) => {
                      const fs = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.warn;
                      const FIcon = f.severity === "fail" ? ErrorOutlineIcon : WarningAmberIcon;
                      const friendly = CODE_FRIENDLY_OVERRIDES[f.code];
                      const displayTitle = friendly?.title || f.title;
                      const displayMessage = friendly?.message || f.message;
                      return (
                        <Box
                          key={i}
                          sx={{
                            display: "flex",
                            gap: 0.75,
                            px: 1,
                            py: 0.75,
                            borderRadius: 0.75,
                            bgcolor: "var(--fx-paper)",
                            border: `1px solid ${fs.border}`,
                          }}
                        >
                          <FIcon sx={{ fontSize: 15, color: fs.fg, mt: "1px" }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 800, color: fs.fg, fontSize: "0.72rem" }}
                              >
                                {displayTitle}
                              </Typography>
                              {/* Backend-emitted finding code (real responses
                                  only — mock validator doesn't populate
                                  `code`). Shown as a small mono badge with a
                                  tooltip explaining the expected DEV-phase
                                  meaning when it's a known code. */}
                              {f.code && (
                                <Tooltip title={CODE_TOOLTIPS[f.code] || `Finding code: ${f.code}`}>
                                  <Box
                                    component="span"
                                    sx={{
                                      fontFamily:
                                        "ui-monospace, SFMono-Regular, Menlo, monospace",
                                      fontSize: "0.6rem",
                                      fontWeight: 700,
                                      letterSpacing: 0.3,
                                      color: fs.fg,
                                      bgcolor: "var(--fx-paper)",
                                      border: `1px solid ${fs.border}`,
                                      borderRadius: 0.5,
                                      px: 0.6,
                                      py: 0.1,
                                      cursor: "help",
                                    }}
                                  >
                                    {f.code}
                                  </Box>
                                </Tooltip>
                              )}
                            </Stack>
                            <Typography
                              variant="caption"
                              sx={{ color: colors.grey?.[700] || "var(--fx-text-secondary)", fontSize: "0.72rem", lineHeight: 1.45, mt: 0.25, display: "block" }}
                            >
                              {displayMessage}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>

        {/* Actions — "Re-run validation" button removed 2026-06-09. Per
            user report, clicking it actually triggered training (same effect
            as the main Start button), which is the opposite of what the
            label suggests and confusing. The button also served no purpose
            distinct from Back to Configuration → re-trigger from there.
            Wholesale removal rather than fixing the handler, since the
            feature itself isn't valuable. */}
        <Stack direction="row" spacing={1} sx={{ mt: 2.5, flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/configs")}
          >
            Back to Configuration
          </Button>
        </Stack>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 2,
            color: colors.grey?.[500] || "var(--fx-text-muted)",
            fontSize: "0.65rem",
            fontStyle: "italic",
          }}
        >
          Prototype mock data review, mirrors the per-site schema validation a
          real backend preflight would attach to a run. No real artifact endpoint
          is queried.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default SchemaValidationDetailsCard;
